import React, { useEffect, useState } from 'react';
import './Home.css';

const SCROLL_TOP_THRESHOLD = 400;

function Home() {
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Show/hide scroll-to-top button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > SCROLL_TOP_THRESHOLD);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    let moved = false;
    const moveFocusToHeading = () => {
      if (moved) return;
      moved = true;
      window.removeEventListener('scroll', onScroll);
      const mainHeading = document.getElementById('main-content-heading');
      if (mainHeading) mainHeading.focus({ preventScroll: true });
    };
    const onScroll = () => {
      if (window.scrollY < 10) moveFocusToHeading();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    setTimeout(moveFocusToHeading, 600);
  };

  // Handle hash navigation on page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.substring(1); // Remove the #
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 92;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="home-page">
      <section className="hero-section" aria-labelledby="main-content-heading">
        <div className="hero-content">
          <div className="hero-accent-line" aria-hidden="true"></div>
          <div className="hero-text">
            <h1 id="main-content-heading" tabIndex={-1}><span>YK2 Partners is a boutique investment partnership focused solely on the Indian public markets</span></h1>
            <p>
              Our investment strategy is to buy high quality businesses with a margin of safety and hold them for a long duration. 
              Our portfolio is built with deep bottom-up, fundamentals-based research and is benchmark agnostic. We are an experienced 
              team with a long track record of generating outperformance in the Indian markets. We aspire to serve long term oriented, 
              like-minded investor partners who share our enthusiasm for the uniquely attractive India opportunity and are aligned with 
              our way of capturing it.
            </p>
          </div>
        </div>
      </section>

      <section id="our-goals" className="goals-section" aria-labelledby="our-goals-heading">
        <div className="section-content">
          <div className="section-heading">
            <div className="accent-line" aria-hidden="true"></div>
            <h2 id="our-goals-heading">Our Goals</h2>
          </div>
          <div className="section-body">
            <ol className="goals-list">
              <li>Generate top tier long-term risk adjusted returns</li>
              <li>Build deep relationships with long-term oriented, like-minded investors</li>
              <li>Create a best-in-class boutique investment partnership dedicated to India: single product, single strategy, capped AUM, low fee.</li>
            </ol>
          </div>
        </div>
      </section>

      <section id="guiding-principles" className="principles-section" aria-labelledby="guiding-principles-heading">
        <div className="section-content">
          <div className="section-heading">
            <div className="accent-line" aria-hidden="true"></div>
            <h2 id="guiding-principles-heading">Guiding Principles</h2>
          </div>
          
          <div className="section-body">
          <div className="principle-item">
            <h3>Invest only in high quality business</h3>
            <p>
              We seek to invest in businesses that generate high ROCE across the business cycle. Typically, such businesses are market share gainers with a strong, defensible moat and a conservative, long-term oriented management who serve as excellent fiduciaries. They are also cautious with leverage, good shepherds of capital, and conservative in accounting and pronouncements. Even amongst those with high reported ROCE, we avoid companies that are government owned, conglomerates, acquisitive, highly levered, facing structural or regulatory threats, losing their competitive advantage, or reputed to have poor corporate governance.
            </p>
          </div>

          <div className="principle-item">
            <h3>Seek margin of safety and don't overpay</h3>
            <p>
              Investing only in high quality businesses is our first line of defence. The second line of defence is to ensure that we do 
              not overpay for such businesses. Quality companies that we seek to own are usually expensive, baking in flawless performance. 
              Then once in a while, these quality businesses falter and are available at reasonable valuations either due to macro events or 
              due to short-term fears related to the business. That is when we try to initiate our investments. Such opportunities are available 
              infrequently and we would be lucky to have 4-5 such opportunities in any year. The real challenge is to wait patiently and do nothing 
              for most of the time.
            </p>
          </div>

          <div className="principle-item">
            <h3>Hold investments for long duration </h3>
            <p>
              Time is a friend of a high quality business. Hence, we try to hold our investments for a long duration, ideally forever. In practice we exit our investments if the valuation becomes egregious or we realise that the quality of the business is worse than our initial assessment. We have this preference for long holding periods for three reasons. First, quality businesses run by competent and honest management teams keep finding ways of creating value over the long term. Second, knowledge and insights into a business are far richer when researched with a permanent owner mindset. Third, our approach of buying high quality businesses with a margin of safety translates into at best 4-5 investment opportunities a year, which is inconsistent with a high turnover portfolio.
            </p>
          </div>

          <div className="principle-item">
            <h3>Think independent, think contrarian, triangulate extensively</h3>
            <p>
              High quality businesses are available at a fair price only when they are going through a rough patch. To take advantage 
              of these situations we need to think independently and be contrarian. It would be impossible to do so without tuning out what 
              others are saying. We do not use sell side recommendations. We heavily filter and discount what we hear in management meetings. 
              We keenly analyse the historical performance of the company. We find conversations with ex-employees, competitors, channel partners, 
              suppliers and customers to be very helpful. We triangulate extensively across a range of inputs to form our own independent view 
              on the business.
            </p>
          </div>

          <div className="principle-item">
            <h3>Focus on micro over macro, and bottom up over top down</h3>
            <p>
              We are fundamental, bottom up investors and focus on the microeconomic drivers of a business. We do not make investments based on macro and top-down views. Our view is that as a long term investor, we have to be prepared for good and bad macro periods. Hence the important thing is to buy resilient businesses with a margin of safety. Similarly, we do not have any view on market valuations. It serves no purpose for us as we are not buying the market. Instead, we are assembling a portfolio of high quality businesses when they are dislocated, often for their own idiosyncratic reasons.
            </p>
          </div>

          <div className="principle-item">
            <h3>No benchmark constraints</h3>
            <p>
              We are not wired to think in benchmarks, tracking error or relative performance. A benchmark oriented approach is inconsistent 
              with our core principles. Due to our focus on quality of business, we have excluded large groups of companies from our investable 
              universe – such as oil & gas, metal and mining, real estate, government owned companies, and conglomerates. Our insistence on a 
              margin of safety precludes us from investing in many high quality companies (such as Asian paints, Hindustan Unilever and Nestle 
              India) which have been perpetual market darlings. Lastly, our willingness to hold cash also results in significant deviations 
              from the index.
            </p>
          </div>

          <div className="principle-item">
            <h3>Concentrate but not too much</h3>
            <p>
              Our natural style, consistent with our investment approach, is to run a concentrated portfolio. However, we would like to 
              strike a balance between over concentrating, which requires more conviction than is warranted given the unexpected changes, 
              externalities, accidents or mistakes that are part and parcel of investing, and over diversifying, which will compromise alpha 
              given the limited number of truly compelling opportunities that are available every year. The right balance for us is a portfolio 
              with approximately 25 stocks. We expect to find not more than 4-5 investment ideas in an average year. While we strive to invest 
              as a permanent owner, based on history, we expect that our average holding period will be around 5 years. This translates into a 
              portfolio with approximately 25 investments.
            </p>
          </div>

          <div className="principle-item">
            <h3>No forecasting; No projections; Think in scenarios</h3>
            <p>
              Precise and detailed modelling and forecasting is not a part of our process. We neither indulge in macro projections nor engage in forecasting revenue, cash flows and EPS for the companies we evaluate or invest in. We take a broad directional view on how the individual businesses might perform over the long-term. We make a very broad range of assumptions to gauge a ballpark estimate of the EPS and FCF that the business could (not would) make 5 years out in base, bull and bear scenarios. We use these scenarios flexibly and dynamically as conscience keepers to guide our decisions on entering or exiting an investment.
            </p>
          </div>

          <div className="principle-item">
            <h3>Fair wage for honest work by craftsmen, not a get rich scheme</h3>
            <p>
              Our aspiration on fees and expenses is to be amongst the lowest offered by any boutique, active manager with capped AUM . We do not view our enterprise in starting YK2 Partners as a "get rich" scheme. Instead we regard ourselves as craftsmen trying to earn a fair wage for honest work. We want to charge a reasonable fee, which is enough to attract and retain a best in class partnership. We also intend to keep fund expenses to a minimum with no frills but all the necessary safeguards needed to be best in class fiduciaries.
            </p>
          </div>

          <div className="principle-item">
            <h3>No marketing; No distributors; No bullshit</h3>
            <p>
              We believe our edge is our ability to stick to our stated strategy. We fully appreciate this is very hard. Hence we only want to partner with those investors who are in sync with who we are, what we are trying to achieve and how we are trying to achieve it. We believe this vision is inconsistent with the traditional marketing mindset of gathering AUM. Hence, we don't spend any time marketing or pursuing distribution arrangements. We believe that if we do a good job eventually a few right investors will find us. To these investors we will provide radical transparency. Like in a successful marriage, we believe anyone who is going to be a long-term partner needs to know exactly what they are getting into.
            </p>
          </div>
          </div>
        </div>
      </section>

      <section id="our-culture" className="culture-section" aria-labelledby="our-culture-heading">
        <div className="section-content">
          <div className="section-heading">
            <div className="accent-line" aria-hidden="true"></div>
            <h2 id="our-culture-heading">Our Culture</h2>
          </div>
          <div className="section-body">
          <div className="culture-item">
            <h3>Doing the right thing</h3>
            <p>
              This pervades everything we do. In our dealings with potential and existing clients, regulators, service providers, team members and companies, we are governed by a strong moral compass about what the right thing to do is. For instance, when speaking with a potential client we begin by discussing why we may not be the right fit for them. For service providers this means treating them fairly and respectfully. When hiring, we always discuss if joining our team is the right decision for the candidate. Similarly, when selecting companies for investments, we look for management teams for whom doing the right thing is paramount.
            </p>
          </div>

          <div className="culture-item">
            <h3>Respect for each other, our shared goals, and guiding principles</h3>
            <p>
              We deeply respect each other. We also share a deep respect for our shared vision and guiding principles, which have brought us together to build the firm. These shared beliefs and mutual respect form the bedrock of an open, non-hierarchical and collaborative team culture. It is reflected in having direct but respectful discussions, even when we are in total disagreement with each other. We debate views, not the person, fully appreciating that the opposing view also seeks the client's best interest. We have been heavily influenced by the McKinsey team environment, where it is the obligation of every team member to dissent when they do not agree. We try to do it with full humility and total respect for each other.
            </p>
          </div>

          <div className="culture-item">
            <h3>Continuous learning and improvement</h3>
            <p>
              A key part of the attraction of the investing profession to us is that it is a lifelong school. We are thankful for the opportunity to learn about industries and businesses every day. We are inquisitive and curious by nature and investment research plays to these traits. We read and listen widely to learn as much as we can. We regularly question our assumptions and hypotheses, especially in the face of new facts, and this usually leads to deeper understanding and newer insights. We believe that investment excellence is a journey, not a destination. As craftsmen, it is important for us to continuously evaluate our tools and see how we might sharpen them.
            </p>
          </div>
          </div>
        </div>
      </section>

      <section id="our-name" className="name-section" aria-labelledby="our-name-heading">
        <div className="section-content">
          <div className="section-heading">
            <div className="accent-line" aria-hidden="true"></div>
            <h2 id="our-name-heading">Our Name</h2>
          </div>
          <div className="section-body">
          <p>
            "YK2" is an abbreviation of "Yogah Karmasu Kaushalam". This phrase is the motto of the Indian Institute of Technology Kharagpur, the alma mater of  majority of the founding partners. "Yogah Karmasu Kaushalam" translates to "Excellence in action is Yoga", implying that doing one's work well is true yoga.
          </p>
          
          <p>
            This idea can be traced back to Sri Krishna's discourse with Arjuna in Chapter 2, Verse 50 of the Bhagavad Gita, a foundational philosophical Indian text. In the larger context of the Gita, the quote urges one to acquire equanimity because such a mind allows one to shed distracting thoughts of the effects of deeds and concentrate on the task ahead.
          </p>
          
          <p>
            As bottom up, fundamental research based, long term oriented, contrarian investors who invest with an owner mindset, we find this philosophy very inspiring and pertinent to our way of thinking. Our focus is on the process rather than outcome. Outcome in investing is a function of skill and luck. We have no control over luck but it is our duty as craftsmen to execute our investment process ("Karmasu") as skillfully ("Kaushalam") as we can, and then accept the outcome with equanimity.
          </p>
          
          <p>
            In essence: focus on the process, pursue excellence in research, accept results with equanimity.
          </p>
          </div>
        </div>
      </section>

      <button
        type="button"
        className={`scroll-to-top ${showScrollTop ? 'scroll-to-top--visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Scroll to top of page"
        aria-hidden={!showScrollTop}
        tabIndex={showScrollTop ? 0 : -1}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M6 12L12 6L18 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
}

export default Home;
